/**
 * ConsultsPage.tsx
 *
 * The Consults worklist — the V5 port of legacy `inquiries_full_list-test.php`
 * (titled "Consults").
 *
 * Page chrome is copied verbatim from the sibling list page
 * `src/pages/PlansPage.tsx`, the Inquiries list this page is styled after (its
 * ListPageLayout > flex column > shrink-0 p-6 header block > min-h-0 flex-1
 * scroll body), so `p-6` encloses the PageHeader.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Lock, Star } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Badge, Button, DataTable, EmptyState, Select } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { formatCurrency } from '@/lib/utils';
import { formatPacificDate } from '@/lib/facilityTimezone';
import { getStageBadgeVariant } from '@/lib/stageBadgeVariant';
import type { SelectOption } from '@/types/common.types';
import { usePageContext } from '@/hooks/usePageContext';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { useLocalDashboardFilters, useFacilityOverrideValues } from '@/pages/dashboard/dashboardFilters';
import { usePlanFilterOptions } from '@/hooks/filters';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import * as consultsSvc from '@/services/consults.service';
import type { ConsultListItem, ConsultSearchType } from '@/services/consults.service';
import {
  CONSULTS_SORT_KEYS,
  CONSULT_ROW_STATUS_STYLES,
  INTEREST_STAR_COUNT,
  buildConsultsFilterConfigs,
  buildConsultsListParams,
  consultsListView,
  filledStars,
  hasActiveConsultsFilters,
  splitDisplayDate,
} from './consultsShared';

/** Legacy's page length (nav-test.js:2803). */
const PAGE_SIZE = 100;

const SEARCH_TYPE_OPTIONS: SelectOption<ConsultSearchType>[] = [
  { value: 'name', label: 'Name' },
  { value: 'planId', label: 'Plan ID' },
  { value: 'patientId', label: 'Patient ID' },
];

// ─── Cells ────────────────────────────────────────────────────────────────────

/** The consult day as the plan's facility sees it, two lines like the Inquiries list's dates. */
function ConsultDate({ row }: { row: ConsultListItem }) {
  if (!row.consultDate) return <span className="text-slate-400">—</span>;
  const formatted = formatPacificDate(row.consultDate, row.facilityTimezone ?? '');
  const parts = splitDisplayDate(formatted);
  if (!parts) return <span className="text-xs">{formatted}</span>;
  return (
    <span className="flex flex-col items-start leading-tight whitespace-nowrap">
      <span className="text-xs">{parts[0]}</span>
      <span className="text-xs text-slate-400">{parts[1]}</span>
    </span>
  );
}

function InterestStars({ interest }: { interest: number }) {
  const filled = filledStars(interest);
  return (
    <span className="inline-flex gap-0.5" role="img" aria-label={`Interest ${filled} of ${INTEREST_STAR_COUNT}`}>
      {Array.from({ length: INTEREST_STAR_COUNT }, (_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={`h-3.5 w-3.5 ${i < filled ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
        />
      ))}
    </span>
  );
}

/** "12 (14%)" — legacy's own card format; the share is omitted at zero. */
function cardValue(count: number, percent: number | null): string {
  return percent === null ? String(count) : `${count} (${percent}%)`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ConsultsPage() {
  const navigate = useNavigate();
  // The row opens the plan, which PatientLink cannot target, so it records the
  // recent-patients visit itself, as the Surgery worklist does.
  const visitPatient = usePatientHistoryStore((s) => s.visit);

  const { roles } = useRoleTitles();
  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';

  const filterConfigs = useMemo(
    () => buildConsultsFilterConfigs(doctorLabel, coordLabel),
    [doctorLabel, coordLabel],
  );

  const filterState = useLocalDashboardFilters(filterConfigs, 'consults');
  const { effectiveValues, isFacilityLocked } = useFacilityOverrideValues(filterState.values);

  const [page, setPage] = useState(1);
  const [searchType, setSearchType] = useState<ConsultSearchType>('name');
  // Legacy opens on Consult Date, newest first (nav-test.js:2804-2805).
  const [sort, setSort] = useState<string>(CONSULTS_SORT_KEYS[0]);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  usePageContext('Consults');

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'consults',
    entityLabel: 'consults',
  });

  // Doctor / coordinator / facility / plan type / status / stage options are
  // the Plans list's.
  const sidebarOptions = usePlanFilterOptions();

  const params = useMemo(
    () =>
      buildConsultsListParams(effectiveValues, filterState.negations, {
        page,
        perPage: PAGE_SIZE,
        sort,
        order,
        searchType,
      }),
    [effectiveValues, filterState.negations, page, sort, order, searchType],
  );

  // Reset to the first page whenever the result set changes underneath.
  const filterSignature = JSON.stringify({ ...params, page: 0 });
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  const { data, isLoading, isPlaceholderData, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['consults-list', params],
    queryFn: () => consultsSvc.listConsults(params),
    placeholderData: (prev) => prev,
  });

  const view = consultsListView({
    isError,
    error,
    hasActiveFilters: hasActiveConsultsFilters(params),
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const summary = data?.meta?.summary;

  const columns: DataTableColumn<ConsultListItem>[] = useMemo(
    () => [
      {
        key: 'consultDate',
        header: 'Consult Date',
        sortable: true,
        width: 'w-[84px]',
        render: (r) => <ConsultDate row={r} />,
      },
      {
        key: 'planDisplayId',
        header: 'Plan ID',
        sortable: true,
        width: 'w-[72px]',
        render: (r) => <span className="text-xs tabular-nums text-slate-700">{r.planDisplayId ?? '—'}</span>,
      },
      {
        key: 'patientName',
        header: 'Patient',
        sortable: true,
        role: 'title',
        render: (r) => <span className="font-medium text-slate-900">{r.patientName || '—'}</span>,
      },
      {
        key: 'facilityName',
        header: 'Facility',
        sortable: true,
        render: (r) => <span className="text-xs text-slate-700">{r.facilityName || '—'}</span>,
      },
      {
        key: 'procedures',
        header: 'Procedure(s)',
        sortable: true,
        truncate: true,
        render: (r) => <span className="text-xs text-slate-700">{r.procedures || '—'}</span>,
      },
      {
        key: 'picsIn',
        header: 'Pics In',
        sortable: true,
        width: 'w-[64px]',
        align: 'center',
        render: (r) =>
          r.picsIn ? (
            <span className="text-xs text-slate-700">Y</span>
          ) : (
            <span className="text-xs font-medium text-red-600">N</span>
          ),
      },
      {
        key: 'planType',
        header: 'Plan Type',
        sortable: true,
        render: (r) => <span className="text-sm text-slate-700">{r.planType || '—'}</span>,
      },
      {
        key: 'coordinatorName',
        header: coordLabel,
        sortable: true,
        render: (r) => <span className="text-slate-700">{r.coordinatorName || '—'}</span>,
      },
      {
        key: 'stage',
        header: 'Stage',
        sortable: true,
        render: (r) =>
          r.stage ? (
            <Badge variant={getStageBadgeVariant(r.stage)} dot size="sm">
              {r.stage}
            </Badge>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          ),
      },
      {
        key: 'doctorName',
        header: doctorLabel,
        sortable: true,
        render: (r) => <span className="text-slate-700">{r.doctorName || '—'}</span>,
      },
      {
        key: 'taskCount',
        header: 'Tasks',
        sortable: true,
        width: 'w-[64px]',
        align: 'right',
        render: (r) => <span className="text-xs tabular-nums text-slate-700">{r.taskCount}</span>,
      },
      {
        key: 'due',
        header: 'Due',
        sortable: true,
        width: 'w-[96px]',
        align: 'right',
        render: (r) => <span className="text-xs tabular-nums text-slate-700">{formatCurrency(r.due)}</span>,
      },
      {
        key: 'inquiryStatus',
        header: 'Inquiry Status',
        sortable: true,
        render: (r) =>
          r.inquiryStatus ? (
            <span className="text-xs text-slate-700">{r.inquiryStatus}</span>
          ) : (
            <em className="text-xs text-slate-400">None</em>
          ),
      },
      {
        key: 'interest',
        header: 'Interest',
        sortable: true,
        width: 'w-[80px]',
        render: (r) => <InterestStars interest={r.interest} />,
      },
    ],
    [doctorLabel, coordLabel],
  );

  const handleSortChange = (key: string, dir: 'asc' | 'desc') => {
    setSort(key);
    setOrder(dir);
    setPage(1);
  };

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={filterConfigs}
          values={effectiveValues}
          options={sidebarOptions}
          negations={filterState.negations}
          onChange={filterState.setFilter}
          onNegateChange={filterState.setNegation}
          onClear={filterState.clearFilter}
          searchCommitMode="blur-or-enter"
          showAIAssistant={false}
          storageKey="consults"
          lockedKeys={isFacilityLocked ? ['facility'] : undefined}
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Consults"
            actions={(
              <>
                {/* Legacy offered this beside the search box. It stays in the
                    header, as on the Surgery worklist: a filter config would
                    persist it and send it as a filter. */}
                <div className="flex items-center gap-2 text-sm">
                  <label htmlFor="consults-search-by" className="text-slate-500">
                    Search by
                  </label>
                  <Select<ConsultSearchType>
                    id="consults-search-by"
                    wrapperClassName="w-36"
                    options={SEARCH_TYPE_OPTIONS}
                    value={searchType}
                    onChange={(v) => {
                      setSearchType(v ?? 'name');
                      setPage(1);
                    }}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download className="h-4 w-4" />}
                  disabled={total === 0 || exporting}
                  loading={exporting}
                  onClick={() => startExport({ ...params })}
                >
                  Export
                </Button>
                {exportModal}
              </>
            )}
          />

          {view.kind === 'table' && (
            <MetricCardGrid columns={6}>
              <StatCard label="Total" value={total} tone="slate" />
              <StatCard
                label="Past Due F/U"
                value={cardValue(summary?.pastDue ?? 0, summary?.pastDuePercent ?? null)}
                tone="amber"
                tooltip="Follow-ups with no plan note in the last 7 days"
              />
              <StatCard
                label="Consult Stage"
                value={cardValue(summary?.consultStage ?? 0, summary?.consultStagePercent ?? null)}
                tone="blue"
              />
              <StatCard
                label="Total Sign-Ups"
                value={cardValue(summary?.signUps ?? 0, summary?.signUpsPercent ?? null)}
                tone="emerald"
              />
              <StatCard
                label="Plan Cancelled"
                value={cardValue(summary?.cancelled ?? 0, summary?.cancelledPercent ?? null)}
                tone="rose"
              />
              <StatCard label="Total Balance" value={formatCurrency(summary?.balance ?? 0)} tone="slate" />
            </MetricCardGrid>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {view.kind === 'refused' ? (
            <EmptyState
              icon={<Lock className="h-8 w-8 text-slate-400" aria-hidden="true" />}
              title={view.title}
              description={view.message}
            />
          ) : view.kind === 'failed' ? (
            <EmptyState
              icon={<AlertTriangle className="h-8 w-8 text-slate-400" aria-hidden="true" />}
              title={view.title}
              description={view.message}
              action={{
                label: 'Retry',
                variant: 'secondary',
                loading: isFetching,
                onClick: () => void refetch(),
              }}
            />
          ) : (
            <DataTable<ConsultListItem>
              data={rows}
              columns={columns}
              keyField="planId"
              loading={isLoading || isPlaceholderData}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              manualSort
              sortKey={sort}
              sortDir={order}
              onSortChange={handleSortChange}
              tableFixed
              className="[&>div]:overflow-x-hidden"
              rowClassName={(row) => CONSULT_ROW_STATUS_STYLES[row.rowStatus]}
              onRowClick={(row) => {
                visitPatient({ patientId: row.patientId, name: row.patientName });
                navigate(`/patients/${row.patientId}/plans/${row.planId}`);
              }}
              emptyTitle={view.emptyTitle}
              emptyDescription={view.emptyDescription}
            />
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}
